'use client'

import React, { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import Link from 'next/link'
import Image from 'next/image'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TarotCardBackground } from '@/components/home/Background/index'
import { useUser } from '@/contexts/UserContext'
import { tarotDeck } from '@/lib/data/tarotDeck'
import { SUBSCRIPTION_PLANS } from '@/config/subscriptionPlans'
import { cn } from '@/lib/utils'
import FooterSection from '@/components/home/FooterSection'

// --- Data ---

const features = [
  {
    image: '/features/1.png',
    bgImage: '/generated/feature-1.jpg',
    label: 'Birth Chart',
    headline: 'Your cosmic blueprint, decoded.',
    body: 'Precise planetary positions and house placements mapped from the moment you were born.',
  },
  {
    image: '/features/2.png',
    bgImage: '/generated/feature-2.jpg',
    label: 'Daily Draws',
    headline: 'A new card every day.',
    body: 'Each draw is interpreted through your natal chart and the day\'s planetary transits.',
  },
  {
    image: '/features/3.png',
    bgImage: '/generated/feature-3.jpg',
    label: 'AI Insights',
    headline: 'Depth, not fortune cookies.',
    body: 'Contextual interpretations that connect your cards to your actual astrological moment.',
  },
]

const testimonials = [
  { text: 'The readings feel genuinely personal — like someone who knows my chart is pulling the cards.', author: 'Sarah M.', role: 'Daily user' },
  { text: 'I\'ve tried every tarot app. This is the first one that connects astrology in a way that makes sense.', author: 'James K.', role: 'Astrology enthusiast' },
  { text: 'The birth chart integration changed how I think about my daily draws entirely.', author: 'Luna R.', role: 'Tarot reader' },
]

const faqs = [
  { q: 'How are readings personalized?', a: 'Every reading uses your natal chart — the exact positions of planets at your birth — combined with current planetary transits to interpret each card in context.' },
  { q: 'Do I need astrology knowledge?', a: 'Not at all. The app handles the astrology. You get clear, actionable insights without needing to know your Saturn return from your solar return.' },
  { q: 'What happens after the free trial?', a: 'After 7 days, you can subscribe to continue with unlimited readings and daily draws, or your account will switch to the free tier.' },
  { q: 'Can I use this on mobile?', a: 'Yes. The experience is fully responsive and designed for mobile first.' },
]

// --- Components ---

function FadeIn({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-medium">
      {children}
    </span>
  )
}

// --- Page ---

export default function Home() {
  const { userProfile } = useUser()
  const href = userProfile ? `/${userProfile.handle}/profile` : '/signup'
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] })
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.8], [1, 0.96])

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen">

      {/* ═══════════════ HERO ═══════════════ */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <TarotCardBackground baseDeck={tarotDeck} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0a] pointer-events-none" />

        <motion.div
          style={{ opacity: heroOpacity, scale: heroScale }}
          className="relative z-10 h-full flex flex-col items-center justify-center px-6"
        >
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-[clamp(2.5rem,8vw,7rem)] font-heading font-extrabold tracking-tighter leading-[0.9] text-center"
          >
            Arcana Vibes
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-6 text-white/45 text-base sm:text-lg max-w-md text-center leading-relaxed"
          >
            Tarot readings shaped by your birth chart and today&rsquo;s transits.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="mt-8"
          >
            <Link href={href}>
              <Button size="xl" className="bg-white hover:bg-white/90 text-[#0a0a0a] rounded-full px-10 text-sm font-medium">
                {userProfile ? 'Your Readings' : 'Start Free Trial'}
              </Button>
            </Link>
          </motion.div>
          {!userProfile && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8 }}
              className="mt-3 text-[11px] text-white/25"
            >
              7 days free · No credit card
            </motion.p>
          )}
        </motion.div>
      </section>

      {/* ═══════════════ FEATURES ═══════════════ */}
      <section className="relative py-32 sm:py-40 px-6">
        <div className="max-w-6xl mx-auto">
          <FadeIn className="text-center mb-20">
            <SectionLabel>How it works</SectionLabel>
            <h2 className="mt-4 text-3xl sm:text-5xl font-heading font-bold tracking-tight">
              Personalized, not generic.
            </h2>
          </FadeIn>

          <div className="space-y-32">
            {features.map((f, i) => (
              <FadeIn key={f.label} delay={0.1}>
                <div className={`flex flex-col ${i % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 md:gap-20`}>
                  <div className="flex-1">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-white/30 font-medium">{f.label}</span>
                    <h3 className="mt-3 text-2xl sm:text-4xl font-heading font-bold tracking-tight leading-tight">
                      {f.headline}
                    </h3>
                    <p className="mt-4 text-white/50 text-base sm:text-lg leading-relaxed max-w-md">
                      {f.body}
                    </p>
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="relative w-full max-w-sm">
                      {/* Atmospheric background image */}
                      <div className="absolute -inset-8 -z-10 rounded-3xl overflow-hidden opacity-40">
                        <Image
                          src={f.bgImage}
                          alt=""
                          fill
                          className="object-cover blur-sm"
                        />
                      </div>
                      {/* App screenshot */}
                      <Image
                        src={f.image}
                        alt={f.label}
                        width={480}
                        height={480}
                        className="rounded-2xl w-full relative z-10 shadow-2xl shadow-black/50"
                      />
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SOCIAL PROOF ═══════════════ */}
      <section className="py-32 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <FadeIn className="text-center mb-16">
            <SectionLabel>What people say</SectionLabel>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
                  <p className="text-white/60 text-sm leading-relaxed italic">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="mt-4 pt-4 border-t border-white/[0.06]">
                    <p className="text-sm font-medium text-white/80">{t.author}</p>
                    <p className="text-xs text-white/30">{t.role}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ PRICING ═══════════════ */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <FadeIn className="text-center mb-16">
            <SectionLabel>Pricing</SectionLabel>
            <h2 className="mt-4 text-3xl sm:text-5xl font-heading font-bold tracking-tight">
              Start free. Upgrade when ready.
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(SUBSCRIPTION_PLANS).map(([key, plan], index) => (
              <FadeIn key={key} delay={index * 0.15}>
                <div className={cn(
                  'p-8 rounded-2xl h-full flex flex-col',
                  plan.id === 'price_free'
                    ? 'bg-white/[0.03] border border-white/[0.06]'
                    : 'bg-white/[0.06] border border-white/[0.12]'
                )}>
                  <h3 className="text-lg font-medium text-white/90">{plan.name}</h3>
                  <p className="mt-2 text-3xl font-bold text-white">
                    ${plan.price}
                    <span className="text-sm font-normal text-white/30">/mo</span>
                  </p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <Check className={cn(
                          'w-4 h-4 mt-0.5 shrink-0',
                          f.included ? 'text-white/60' : 'text-white/15'
                        )} />
                        <span className={f.included ? 'text-white/60' : 'text-white/20'}>
                          {f.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Link href={userProfile ? '/upgrade' : '/signup'} className="mt-8 block">
                    <Button
                      className={cn(
                        'w-full rounded-full',
                        plan.id === 'price_free'
                          ? 'bg-white/10 hover:bg-white/15 text-white border border-white/10'
                          : 'bg-white hover:bg-white/90 text-[#0a0a0a]'
                      )}
                      size="lg"
                    >
                      {plan.id === 'price_free' ? 'Get Started' : 'Upgrade'}
                    </Button>
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="py-32 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto">
          <FadeIn className="text-center mb-16">
            <SectionLabel>FAQ</SectionLabel>
          </FadeIn>
          <div className="space-y-0 divide-y divide-white/[0.06]">
            {faqs.map((faq, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <details className="group py-6">
                  <summary className="flex items-center justify-between cursor-pointer list-none text-sm sm:text-base font-medium text-white/80 hover:text-white transition-colors">
                    {faq.q}
                    <span className="ml-4 text-white/20 group-open:rotate-45 transition-transform duration-300 text-lg">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-white/40 leading-relaxed pr-8">
                    {faq.a}
                  </p>
                </details>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="py-32 px-6">
        <FadeIn className="text-center">
          <h2 className="text-3xl sm:text-5xl font-heading font-bold tracking-tight">
            Ready to draw?
          </h2>
          <p className="mt-4 text-white/40 max-w-md mx-auto">
            Your first reading is free. No credit card required.
          </p>
          <div className="mt-8">
            <Link href={href}>
              <Button size="xl" className="bg-white hover:bg-white/90 text-[#0a0a0a] rounded-full px-10 text-sm font-medium">
                {userProfile ? 'Your Readings' : 'Start Free Trial'}
              </Button>
            </Link>
          </div>
        </FadeIn>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <FooterSection />
    </div>
  )
}
